import el from "@cypherpotato/el";
import { api } from "../../api";
import { qrSvg } from "../../components/qr-code";
import { Button, InlineButton, PrimaryButton } from "../../components/button";
import { FormControl, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { TabControl } from "../../components/tabcontrol";
import { BlockCodeWrapper, MutedText, Paragraph } from "../../components/text";
import { Warning } from "../../components/warn";
import { displayAlert } from "../../ui/alert";
import { formatDateTime } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { promptTotpCode, TotpCodeInput } from "../../components/totp-code-input";
import { cond } from "../../util";

export function showTwoFactorDialog(onChange) {
    const view = el.state.create("loading");
    const keys = el.state.create([]);
    const enrollment = el.state.create(null);
    const step = el.state.create("initial");
    const enrollmentError = el.state.create(null);

    function publish(nextKeys) {
        keys.value = nextKeys;
        onChange(nextKeys.map(key => ({
            id: key.id,
            name: key.name,
            createdAt: key.createdAt
        })));
    }

    async function refresh() {
        const result = await api.getJson("/api/v1/accounts/security");
        if (result?.error) {
            view.value = "error";
            return;
        }

        publish(result.data?.keys || []);
        view.value = "list";
    }

    async function startEnrollment() {
        await runOperation("Creating authenticator...", async () => {
            const result = await api.postJson("/api/v1/accounts/security/totp", {});
            if (!result?.data?.secret) return;

            const qr = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(qrSvg(result.data.otpAuthUri));
            const name = `TOTP Key #${keys.value.length + 1}`;
            enrollment.value = {
                secret: result.data.secret,
                qr,
                requiresCurrent: keys.value.length > 0,
                nameInput: TextInput("totpName", name, { value: name }),
                codeInput: TotpCodeInput(),
                currentInput: TotpCodeInput()
            };
            step.value = "initial";
            enrollmentError.value = null;
            view.value = "enroll";
        });
    }

    async function removeKey(key) {
        try {
            const code = await promptTotpCode(
                keys.value.length === 1
                    ? "Enter a current code to remove the last authenticator and disable two-factor authentication."
                    : `Enter a current code to remove ${key.name}.`
            );
            await runOperation("Removing authenticator...", async () => {
                const result = await api.postJson(`/api/v1/accounts/security/totp/${key.id}/remove`, { code });
                if (result?.error) return;
                publish(result.data?.keys || []);
            });
        } catch {
            return;
        }
    }

    displayAlert({
        title: "Two-factor authentication",
        dialogSize: "small",
        message: el.state.listening([view, keys, enrollment], () => {
            if (view.value === "loading") {
                return Paragraph("Loading authenticators...");
            }

            if (view.value === "error") {
                return Paragraph("Could not load authenticators.");
            }

            if (view.value === "enroll" && enrollment.value) {
                const current = enrollment.value;

                const goTo = nextStep => {
                    enrollmentError.value = null;
                    step.value = nextStep;
                };

                const closeEnrollment = () => {
                    enrollment.value = null;
                    view.value = "list";
                };

                const confirm = () => runOperation("Adding authenticator...", async () => {
                    const result = await api.postJson("/api/v1/accounts/security/totp/confirm", {
                        code: current.codeInput.value,
                        name: current.nameInput.value,
                        currentCode: current.requiresCurrent ? current.currentInput.value : null
                    }, { suppressError: true });

                    if (result?.error) {
                        enrollmentError.value = result.error;
                        return;
                    }

                    publish(result.data?.keys || []);
                    goTo("success");
                });

                const requireCode = (input, onValid) => {
                    if (!/^\d{6}$/.test(input.value)) {
                        enrollmentError.value = "Enter the 6-digit authentication code.";
                        return;
                    }
                    onValid();
                };

                return el.state.listening([step, enrollmentError], () => el("div.totp-enrollment",
                    cond(
                        [step.value === "initial", () => el.fragment(
                            Paragraph("Scan the QR code with your authenticator app, or enter the secret manually."),
                            el("div.totp-enrollment-tabs",
                                TabControl([
                                    {
                                        title: "QR code",
                                        contents: el("img.totp-qr", { src: current.qr, alt: "Authenticator QR code" })
                                    },
                                    {
                                        title: "Secret",
                                        contents: BlockCodeWrapper("Secret", current.secret)
                                    }
                                ])
                            ),
                            Warning(Paragraph("Save this secret now. It is not shown again after confirmation.")),
                            el("div.totp-actions",
                                Button("Cancel", { type: "button", onClick: closeEnrollment }),
                                PrimaryButton("Continue", Icon("arrow-right-line"), {
                                    type: "button",
                                    onClick() {
                                        goTo("check");
                                    }
                                })
                            )
                        )],
                        [step.value === "check", () => el.fragment(
                            Paragraph("Type the 6-digit code your authenticator app shows."),
                            FormControl("Name", current.nameInput),
                            FormControl("Authentication code", current.codeInput),
                            enrollmentError.value && Warning(Paragraph(enrollmentError.value)),
                            el("div.totp-actions",
                                InlineButton(Icon("arrow-left-line"), "Back", {
                                    type: "button",
                                    onClick() {
                                        goTo("initial");
                                    }
                                }),
                                PrimaryButton("Add authenticator", {
                                    type: "button",
                                    onClick() {
                                        requireCode(current.codeInput, () =>
                                            current.requiresCurrent ? goTo("auth-2fa") : confirm());
                                    }
                                })
                            )
                        )],
                        [step.value === "auth-2fa", () => el.fragment(
                            Paragraph("Confirm with a code from an authenticator already linked to this account."),
                            FormControl("Current authenticator code", current.currentInput),
                            enrollmentError.value && Warning(Paragraph(enrollmentError.value)),
                            el("div.totp-actions",
                                InlineButton(Icon("arrow-left-line"), "Back", {
                                    type: "button",
                                    onClick() {
                                        goTo("check");
                                    }
                                }),
                                PrimaryButton("Confirm", {
                                    type: "button",
                                    onClick() {
                                        requireCode(current.currentInput, confirm);
                                    }
                                })
                            )
                        )],
                        [step.value === "success", () => el.fragment(
                            el("div.totp-enrollment-success",
                                Icon("checkbox-circle-line"),
                                Paragraph("Authenticator added. Sign-in now asks for a code from it.")
                            ),
                            el("div.totp-actions",
                                PrimaryButton("Done", { type: "button", onClick: closeEnrollment })
                            )
                        )]
                    )
                ));
            }

            return el("div.totp-key-list",
                keys.value.length === 0
                    ? MutedText("No authenticators yet. Sign-in asks for a code after you add one.")
                    : keys.value.map(key =>
                        el("div.totp-key-row",
                            el("div",
                                el("strong", key.name || "Authenticator"),
                                el("span", formatDateTime(key.createdAt))
                            ),
                            Button(Icon("delete-bin-line"), "Remove", {
                                type: "button",
                                onClick() {
                                    removeKey(key);
                                }
                            })
                        )
                    ),
                el("div.totp-actions",
                    PrimaryButton(Icon("add-line"), "Add authenticator", {
                        type: "button",
                        onClick() {
                            startEnrollment();
                        }
                    })
                )
            );
        })
    });

    refresh();
}
